import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Share2, Copy, Trash2, Loader2, Globe } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export function ShareDialog({ boardId }) {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const { authedFetch } = useAuth();

  const activeToken = shares[0]?.token || null;
  const getShareUrl = (token) => `${window.location.origin}/p/${token}`;

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard');
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success('Link copied to clipboard');
    }
  };

  const createLink = useCallback(async ({ copy = false } = {}) => {
    setWorking(true);
    try {
      const result = await authedFetch(`/api/boards/${boardId}/shares`, { method: 'POST' });
      setShares((s) => [result, ...s]);
      if (copy) copyToClipboard(getShareUrl(result.token));
      else toast.success('Read-only link created');
      return result;
    } catch (e) {
      toast.error('Failed to create share link');
    } finally {
      setWorking(false);
    }
  }, [authedFetch, boardId]);

  // On open: load links and auto-generate one if none exists yet (one-click share).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await authedFetch(`/api/boards/${boardId}/shares`);
        const active = (data || []).filter((s) => !s.revoked_at);
        if (cancelled) return;
        setShares(active);
        if (active.length === 0) await createLink({ copy: false });
      } catch (e) {
        if (!cancelled) console.error('Failed to fetch shares:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, boardId]);

  const handleRevoke = async (shareId) => {
    try {
      await authedFetch(`/api/shares/${shareId}`, { method: 'DELETE' });
      setShares((s) => s.filter((x) => x.id !== shareId));
      toast.success('Link revoked');
    } catch (e) {
      toast.error('Failed to revoke');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="share-board-open-dialog-button">
          <Share2 className="h-3.5 w-3.5" />
          Share read-only
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-board-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Share read-only link
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Preparing your link…
            </div>
          ) : activeToken ? (
            <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                Anyone with this link can view the board (read-only) — no account needed.
              </p>
              <div className="flex gap-2">
                <Input
                  value={getShareUrl(activeToken)}
                  readOnly
                  className="text-xs font-mono"
                  data-testid="share-board-url-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(getShareUrl(activeToken))}
                  data-testid="share-board-copy-button"
                  className="gap-1.5"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => createLink({ copy: true })}
              disabled={working}
              className="w-full gap-1.5"
              data-testid="share-board-generate-button"
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
              Generate read-only link
            </Button>
          )}

          {shares.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active links</p>
              {shares.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between rounded-lg border p-2 text-sm gap-2"
                  data-testid={`share-row-${s.id}`}
                >
                  <p className="font-mono text-xs truncate flex-1 min-w-0">/p/{s.token}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => copyToClipboard(getShareUrl(s.token))}
                      data-testid={`share-copy-${s.id}`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleRevoke(s.id)}
                      data-testid={`share-board-revoke-button-${s.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => createLink({ copy: true })}
                disabled={working}
                className="w-full gap-1.5"
                data-testid="share-board-new-link-button"
              >
                {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
                Create another link
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
